import { createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "text-sm text-red-600" }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "block text-sm font-medium text-gray-700 mb-1" }
const _hoisted_5 = {
  key: 0,
  class: "text-red-600"
}
const _hoisted_6 = { class: "text-sm text-red-600" }
const _hoisted_7 = { class: "text-sm text-red-600" }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = {
  key: 0,
  class: "py-4"
}
const _hoisted_10 = { class: "text-sm text-red-600" }
const _hoisted_11 = {
  key: 1,
  class: "flex flex-col items-center py-8 gap-3"
}
const _hoisted_12 = {
  key: 2,
  class: "py-4"
}
const _hoisted_13 = {
  key: 3,
  class: "py-2"
}
const _hoisted_14 = { class: "grid grid-cols-4 gap-4 text-center" }
const _hoisted_15 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_16 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_17 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_18 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_19 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_20 = {
  key: 0,
  class: "mt-2 max-h-48 overflow-y-auto text-sm text-gray-700 space-y-1"
}

import Cookies from 'js-cookie'
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'

import BasicSpinner from '@/components/BasicSpinner.vue'
import FormElement from '@/components/neue/FormElement.vue'
import FormSection from '@/components/neue/FormSection.vue'
import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import TheModal from '@/components/TheModal.vue'
import { trackImportRun } from '@/helpers/importRunTracker'
import { buildMatchingPayload, FIELDS_BY_TYPE, missingRequiredKeys } from '@/helpers/importWizard'
import api from '@/store/api'

const MAX_POLL_FAILURES = 5


export default {
  __name: 'ImportWizardModal',
  props: {
  isOpen: { type: Boolean, default: false },
  importType: { type: String, required: true }
},
  emits: ['close', 'completed'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const step = ref('upload')
const fields = computed(() => FIELDS_BY_TYPE[props.importType] || [])
const modalTitle = computed(() => (props.importType === 'ProductsImport' ? 'Import products' : 'Import customers'))

// Upload step
const selectedFile = ref(null)
const selectedStoreId = ref(null)
const batchTag = ref('')
const stores = ref([])
const storeOptions = computed(() => stores.value.map(store => ({ value: store.id, name: store.attributes.store_name })))
const uploading = ref(false)
const uploadError = ref('')

const canUpload = computed(() => {
  if (!selectedFile.value) return false
  if (props.importType === 'ProductsImport') return !!selectedStoreId.value
  return true
})

const onFileChange = e => {
  selectedFile.value = e.target.files[0] || null
}

const loadStores = async () => {
  try {
    const response = await api.call('GET', 'stores?included=none', {}, { receive: false })
    stores.value = response.data.data
  } catch (error) {
    uploadError.value = "We couldn't load your stores. Please close this and try again."
  }
}

// Map step
const importRecord = ref(null)
const mappingValues = reactive({})
const mapError = ref('')

const csvColumnHeaders = computed(() => importRecord.value?.attributes?.csv_column_headers || [])
const sampleRows = computed(() => importRecord.value?.attributes?.sample_rows || [])
const sampleValueFor = index => (sampleRows.value[0] || [])[index]
const hasSampleValue = index => ![undefined, null, ''].includes(sampleValueFor(index))
const columnOptions = computed(() => [
  { value: null, name: 'Not in my file' },
  ...csvColumnHeaders.value.map((header, index) => ({
    value: index,
    name: hasSampleValue(index) ? `${header} e.g. ${sampleValueFor(index)}` : header
  }))
])

const missingRequired = computed(() => missingRequiredKeys(props.importType, mappingValues))

const upload = async () => {
  if (!canUpload.value || uploading.value) return
  uploading.value = true
  uploadError.value = ''

  const form = new FormData()
  form.append('file', selectedFile.value)
  form.append('type', props.importType)

  try {
    const response = await fetch(process.env.VUE_APP_API_SERVER + 'imports', {
      method: 'POST',
      headers: { Authorization: `Bearer ${Cookies.get('auth_token')}` },
      body: form
    })
    const payload = await response.json()
    if (!response.ok) {
      uploadError.value = (payload.errors || ['Upload failed']).join(', ')
      return
    }
    importRecord.value = payload.data
    Object.keys(mappingValues).forEach(key => delete mappingValues[key])
    Object.assign(mappingValues, payload.data.attributes.suggested_column_matching || {})
    fields.value.forEach(field => {
      if (!(field.key in mappingValues)) mappingValues[field.key] = null
    })
    step.value = 'map'
  } catch (error) {
    uploadError.value = 'Upload failed'
  } finally {
    uploading.value = false
  }
}

// Run step
const startingRun = ref(false)
const runStatus = ref('running')
const summary = ref({ created: 0, updated: 0, skipped: 0, failed: 0 })
const failedRows = ref([])
const failedRowsExpanded = ref(false)
let pollTimer = null
const pollGeneration = ref(0)
const pollFailureCount = ref(0)
const pollError = ref('')
const startRun = async () => {
  if (missingRequired.value.length || startingRun.value) return
  startingRun.value = true
  mapError.value = ''

  try {
    await api.call(
      'POST',
      `imports/${importRecord.value.id}/import_runs`,
      {
        column_matching: buildMatchingPayload(mappingValues),
        store_id: selectedStoreId.value || undefined,
        batch_tag: batchTag.value || undefined
      },
      { receive: false }
    )
    runStatus.value = 'running'
    step.value = 'run'
    // Invalidate any stray chain from a previous run before starting a fresh one.
    stopPolling()
    poll()
  } catch (error) {
    mapError.value = (error.response?.data?.errors || ['Import failed to start']).join(', ')
  } finally {
    startingRun.value = false
  }
}

const poll = async () => {
  const gen = pollGeneration.value
  try {
    const response = await api.call('GET', `imports/${importRecord.value.id}`, {}, { receive: false })
    // The wizard was closed, reset, or a new poll chain started while this request was in flight.
    // Bail out without writing state or rearming the timer.
    if (gen !== pollGeneration.value) return

    pollFailureCount.value = 0

    const latestRun = response.data.data.attributes.latest_run

    if (!latestRun || !['completed', 'failed'].includes(latestRun.status)) {
      pollTimer = setTimeout(poll, 2500)
      return
    }

    runStatus.value = latestRun.status
    if (latestRun.status === 'completed') {
      summary.value = latestRun.summary || { created: 0, updated: 0, skipped: 0, failed: 0 }
      failedRows.value = latestRun.failed_rows || []
    }
  } catch (error) {
    if (gen !== pollGeneration.value) return

    pollFailureCount.value += 1
    if (pollFailureCount.value < MAX_POLL_FAILURES) {
      pollTimer = setTimeout(poll, 2500)
    } else {
      pollError.value = "We couldn't check on your import status. Please close this and check back shortly."
    }
  }
}

const stopPolling = () => {
  pollGeneration.value += 1
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = null
}

const resetState = () => {
  stopPolling()
  step.value = 'upload'
  selectedFile.value = null
  selectedStoreId.value = null
  batchTag.value = ''
  uploading.value = false
  uploadError.value = ''
  importRecord.value = null
  Object.keys(mappingValues).forEach(key => delete mappingValues[key])
  mapError.value = ''
  startingRun.value = false
  runStatus.value = 'running'
  summary.value = { created: 0, updated: 0, skipped: 0, failed: 0 }
  failedRows.value = []
  failedRowsExpanded.value = false
  pollFailureCount.value = 0
  pollError.value = ''
}

// A run that is still going when the modal closes is handed to the tracker,
// which outlives this component - otherwise closing the wizard would abandon an
// import the merchant is still waiting on.
const handOffRun = () => {
  if (step.value !== 'run' || runStatus.value !== 'running') return
  if (!importRecord.value || !importRecord.value.id) return

  trackImportRun({
    importId: importRecord.value.id,
    importType: props.importType,
    call: (...args) => api.call(...args)
  })
}

const close = () => {
  handOffRun()
  stopPolling()
  emit('close')
}

const finish = () => {
  const wasCompleted = runStatus.value === 'completed'
  resetState()
  if (wasCompleted) emit('completed')
  emit('close')
}

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      resetState()
      if (props.importType === 'ProductsImport') loadStores()
    }
  }
)

onBeforeUnmount(stopPolling)

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(TheModal, {
    "is-open": props.isOpen,
    "modal-title": modalTitle.value,
    "max-width": "max-w-2xl",
    onCloseModal: close
  }, {
    main: _withCtx(() => [
      (step.value === 'upload')
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(FormSection, null, {
              default: _withCtx(() => [
                _createVNode(FormElement, null, {
                  default: _withCtx(() => [
                    _cache[4] || (_cache[4] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "CSV file", -1)),
                    _createElementVNode("input", {
                      type: "file",
                      accept: ".csv",
                      class: "block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100",
                      onChange: onFileChange
                    }, null, 32)
                  ]),
                  _: 1
                }),
                (props.importType === 'ProductsImport')
                  ? (_openBlock(), _createBlock(FormElement, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(SelectInput, {
                          modelValue: selectedStoreId.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((selectedStoreId).value = $event)),
                          name: "Store",
                          placeholder: "Select a store",
                          options: storeOptions.value
                        }, null, 8, ["modelValue", "options"])
                      ]),
                      _: 1
                    }))
                  : (_openBlock(), _createBlock(FormElement, { key: 1 }, {
                      default: _withCtx(() => [
                        _createVNode(InputField, {
                          modelValue: batchTag.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((batchTag).value = $event)),
                          name: "Tag these customers (optional)"
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })),
                (uploadError.value)
                  ? (_openBlock(), _createBlock(FormElement, { key: 2 }, {
                      default: _withCtx(() => [
                        _createElementVNode("p", _hoisted_2, _toDisplayString(uploadError.value), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]))
        : (step.value === 'map')
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(FormSection, {
                name: "Match your columns",
                description: "Tell us which column in your file maps to each field."
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields.value, (field) => {
                    return (_openBlock(), _createBlock(FormElement, {
                      key: field.key
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_4, [
                          _createTextVNode(_toDisplayString(field.label) + " ", 1),
                          (field.required)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_5, "*"))
                            : _createCommentVNode("", true)
                        ]),
                        _createVNode(SelectInput, {
                          modelValue: mappingValues[field.key],
                          "onUpdate:modelValue": $event => ((mappingValues[field.key]) = $event),
                          options: columnOptions.value
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "options"])
                      ]),
                      _: 2
                    }, 1024))
                  }), 128)),
                  (missingRequired.value.length)
                    ? (_openBlock(), _createBlock(FormElement, { key: 0 }, {
                        default: _withCtx(() => [
                          _createElementVNode("p", _hoisted_6, "Missing required fields: " + _toDisplayString(missingRequired.value.join(', ')), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (mapError.value)
                    ? (_openBlock(), _createBlock(FormElement, { key: 1 }, {
                        default: _withCtx(() => [
                          _createElementVNode("p", _hoisted_7, _toDisplayString(mapError.value), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]))
          : (step.value === 'run')
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (pollError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("p", _hoisted_10, _toDisplayString(pollError.value), 1)
                    ]))
                  : (runStatus.value === 'running')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createVNode(BasicSpinner, { size: "medium" }),
                        _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Importing your file, this may take a moment...", -1))
                      ]))
                    : (runStatus.value === 'failed')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[6] || (_cache[6] = [
                          _createElementVNode("p", { class: "text-sm text-red-600" }, "The import failed to complete. Please check your file and try again.", -1)
                        ]))]))
                      : (runStatus.value === 'completed')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                              _createElementVNode("div", null, [
                                _createElementVNode("p", _hoisted_15, _toDisplayString(summary.value.created), 1),
                                _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Created", -1))
                              ]),
                              _createElementVNode("div", null, [
                                _createElementVNode("p", _hoisted_16, _toDisplayString(summary.value.updated), 1),
                                _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Updated", -1))
                              ]),
                              _createElementVNode("div", null, [
                                _createElementVNode("p", _hoisted_17, _toDisplayString(summary.value.skipped), 1),
                                _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Skipped", -1))
                              ]),
                              _createElementVNode("div", null, [
                                _createElementVNode("p", _hoisted_18, _toDisplayString(summary.value.failed), 1),
                                _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Failed", -1))
                              ])
                            ]),
                            (failedRows.value.length)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                  _createElementVNode("button", {
                                    class: "text-sm font-medium text-brand-700",
                                    onClick: _cache[2] || (_cache[2] = $event => (failedRowsExpanded.value = !failedRowsExpanded.value))
                                  }, _toDisplayString(failedRowsExpanded.value ? 'Hide' : 'Show') + " failed rows (" + _toDisplayString(failedRows.value.length) + ") ", 1),
                                  (failedRowsExpanded.value)
                                    ? (_openBlock(), _createElementBlock("ul", _hoisted_20, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(failedRows.value, (failedRow, index) => {
                                          return (_openBlock(), _createElementBlock("li", { key: index }, " Row " + _toDisplayString(failedRow.row) + ": " + _toDisplayString(failedRow.message), 1))
                                        }), 128))
                                      ]))
                                    : _createCommentVNode("", true)
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      (step.value === 'upload')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(NeueButton, {
              type: "secondary",
              onClick: close
            }, {
              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }),
            _createVNode(NeueButton, {
              type: canUpload.value ? 'primary' : 'inactive',
              loading: uploading.value,
              onClick: upload
            }, {
              default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                _createTextVNode(" Continue ", -1)
              ]))]),
              _: 1
            }, 8, ["type", "loading"])
          ], 64))
        : (step.value === 'map')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(NeueButton, {
                type: "secondary",
                onClick: _cache[3] || (_cache[3] = $event => (step.value = 'upload'))
              }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode("Back", -1)
                ]))]),
                _: 1
              }),
              _createVNode(NeueButton, {
                type: missingRequired.value.length ? 'inactive' : 'primary',
                loading: startingRun.value,
                onClick: startRun
              }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createTextVNode(" Import ", -1)
                ]))]),
                _: 1
              }, 8, ["type", "loading"])
            ], 64))
          : (step.value === 'run')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                (pollError.value || runStatus.value !== 'running')
                  ? (_openBlock(), _createBlock(NeueButton, {
                      key: 0,
                      type: "primary",
                      onClick: finish
                    }, {
                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode("Done", -1)
                      ]))]),
                      _: 1
                    }))
                  : (_openBlock(), _createBlock(NeueButton, {
                      key: 1,
                      type: "secondary",
                      onClick: close
                    }, {
                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode("Close and keep importing", -1)
                      ]))]),
                      _: 1
                    }))
              ], 64))
            : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["is-open", "modal-title"]))
}
}

}